import { FC } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useFormContext } from 'old-rhf';

import { Bold, CheckboxBase, Flex, Regular, Spacer } from '@zazume/zzm-base';

import { PhoneInput } from '#/components/forms/input/PhoneInput';
import { IbanFieldWithVoPLegacy } from '#/components/iban';
import { Can } from '#/components/roles/Can';
import { FiscalInformationValidator, OldManualValidationToggle } from '#/components/verify';
import { useIdCardOptions } from '#/utils/idCard';
import { Config } from '#lib/Config';

import { NationalitySelect } from '../../../../../components/form/select/nationalitySelect/NationalitySelect';
import { Input, Select } from '../../../../../components/forms';
import GooglePlacesAutocomplete from '../../../../../components/maps/GooglePlacesAutocomplete';
import { useI18n } from '../../../../../hooks/useI18n';
import { Provider, DocumentIDType, ProviderServicesTypes } from '../../../../../models';
import { ResponsiveModalForm } from '../../../../../templates/ResponsiveModalForm';
import { ProviderFormInputs } from './Provider.schema';

const CheckboxContainer = styled.div(
  ({ theme }) => css`
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;

    ${theme.mq.tablet} {
      flex-direction: row;
      gap: 4.25rem;
    }
  `
);

const CheckboxColumn = styled.div(
  ({ theme }) => css`
    width: 100%;

    & > * {
      margin-bottom: 1.5rem;
      padding-top: 0.1875rem;
      padding-bottom: 0.375rem;
    }

    ${theme.mq.tablet} {
      width: 50%;
    }
  `
);

const servicesMiddleIndex = Math.ceil(ProviderServicesTypes.length / 2);
const editableArray = [...ProviderServicesTypes];
const SERVICES_GROUP_1 = editableArray.splice(0, servicesMiddleIndex);
const SERVICES_GROUP_2 = editableArray.splice(-servicesMiddleIndex);

interface ProviderFormProps {
  provider?: Provider;
}

export const ProviderForm: FC<ProviderFormProps> = ({ provider }) => {
  const { t } = useI18n();
  const idCardOptions = useIdCardOptions();
  const { Body: FormBody, Header: FormHeader, Row: FormRow, Column: FormColumn } = ResponsiveModalForm;
  const {
    watch,
    register,
    formState: { errors, dirtyFields },
    setValue,
    setError,
    clearErrors
  } = useFormContext<ProviderFormInputs>();
  const idNumber = watch('idNumber');
  const documentIDType = watch('documentIDType');
  const name = watch('name');
  const isEditing = Boolean(provider);

  const defaultValueIDType = idCardOptions.find(option => option.value === provider?.billingData?.documentIDType);

  return (
    <ResponsiveModalForm>
      <FormBody>
        <FormHeader>
          <Bold variant="XL">{t('providers.common.details')}</Bold>
        </FormHeader>
        <FormRow>
          <FormColumn>
            <Input
              name="name"
              ref={register}
              label={t('providers.common.taxName')}
              type="text"
              errors={errors.name}
              defaultValue={provider?.name}
              placeholder={t('general.forms.placeholders.example', { value: 'Enterprise SL' })}
            />
            <Select
              name="documentIDType"
              ref={register}
              label={t('documentsModals.documentType')}
              options={idCardOptions}
              errors={errors.documentIDType}
              defaultValue={defaultValueIDType}
            />
            <GooglePlacesAutocomplete
              name="fullAddress"
              apiKey={Config.GOOGLE_MAPS_API_KEY}
              ref={register}
              label={t('general.address')}
              setValue={setValue}
              setErrors={setError}
              clearErrors={clearErrors}
              errors={errors.fullAddress}
              defaultValue={provider?.billingData?.fullAddress}
              isOptional
              placeholder={t('general.forms.placeholders.address')}
            />
            <NationalitySelect name="nationality" />
          </FormColumn>
          <FormColumn>
            <PhoneInput
              name="phone"
              label={t('general.phone')}
              type="phone"
              isOptional
              placeholder={t('general.forms.placeholders.phone')}
            />
            <Input
              name="idNumber"
              ref={register}
              label={t('general.idNumber')}
              type="text"
              errors={errors.idNumber}
              defaultValue={provider?.billingData?.idNumber}
              isOptional
              placeholder={t('general.forms.placeholders.example', { value: 'B1234323' })}
            />
            <Input
              name="email"
              ref={register}
              label={t('general.email')}
              type="text"
              errors={errors.email}
              defaultValue={provider?.email}
              isOptional
              placeholder={t('general.forms.placeholders.example', { value: 'alex@email.com' })}
            />
            <IbanFieldWithVoPLegacy
              ref={register}
              name="iban"
              legalName={watch('name')}
              ibanValue={watch('iban')}
              error={errors.iban}
              defaultValue={provider?.billingData?.iban}
              isOptional
              placeholder={t('general.forms.placeholders.example', { value: 'ES384798948748392' })}
            />
          </FormColumn>
        </FormRow>
        <FormHeader>
          <Flex gap="md" align="center">
            <Bold variant="XL">{t('providers.modals.edition.services')}</Bold>
            <Regular color="Gray400" variant="XL">
              {t('general.optional')}
            </Regular>
          </Flex>
        </FormHeader>
        <FormRow>
          <FormColumn>
            <CheckboxContainer>
              <CheckboxColumn>
                {SERVICES_GROUP_1.map(serviceKey => (
                  <CheckboxBase
                    key={serviceKey}
                    ref={register}
                    name={`services.${serviceKey}`}
                    defaultChecked={provider?.services[serviceKey]}
                  >
                    {t(`providers.services.${serviceKey}`)}
                  </CheckboxBase>
                ))}
              </CheckboxColumn>
              <CheckboxColumn>
                {SERVICES_GROUP_2.map(serviceKey => (
                  <CheckboxBase
                    key={serviceKey}
                    ref={register}
                    name={`services.${serviceKey}`}
                    defaultChecked={provider?.services[serviceKey]}
                  >
                    {t(`providers.services.${serviceKey}`)}
                  </CheckboxBase>
                ))}
              </CheckboxColumn>
            </CheckboxContainer>
          </FormColumn>
        </FormRow>
        <Can hasRoles={['admin']}>
          <Spacer vertical="s8" />
          <FiscalInformationValidator
            validatedAt={provider?.billingData?.validatedAt}
            validatedFrom={provider?.billingData?.validatedFrom}
            idNumber={idNumber || ''}
            name={name || ''}
            idNumberType={(documentIDType as DocumentIDType) || ''}
            dirtyFields={{
              idNumber: dirtyFields.idNumber,
              idNumberType: false,
              name: dirtyFields.name
            }}
            manualValidationToggle={<OldManualValidationToggle register={register} />}
            manualValidationAvailable={isEditing}
          />
        </Can>
        <Spacer vertical="s16" />
      </FormBody>
    </ResponsiveModalForm>
  );
};
